import React, { useCallback } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, HighlightWords, useResponsive } from '@garnish/core';
import { useOpenURL } from '@garnish/navigation';

import { useTelemetry } from '@order/Telemetry';

import { type useIdMeVerification } from '../../../../integrations/idme';
import { type useIdMeVerificationContent } from '../../hooks';
import { IdMeLogoLockup } from '../IdMeLogoLockup';
import { IdMeVerifyButton } from '../IdMeVerifyButton';

// ─────────────────────────────────────────────────────────────────────────────

export const IdMeVerificationPrompt = (props: Props) => {
  const { verification, content } = props;

  const { isVerified, isVerifying, isPreparingRequest, isAuthorizing } = verification;
  const { isSigningOut, canSignOut, verifiedGroups, verify, signOut } = verification;
  const { partnerLogo, learnMoreUrl } = content;

  const { match } = useResponsive();
  const openURL = useOpenURL();
  const { track } = useTelemetry();
  const { entry_source: entrySource } = useLocalSearchParams<RouteParams>();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isCompactLayout = match([true, false]);
  const isVerifyLoading = isVerifying || isPreparingRequest;
  const isSwitchAccountVisible = canSignOut || isSigningOut;

  const groupNames = content.getGroupNames(verifiedGroups);
  const description = isVerified ? content.getVerifiedDescription(groupNames) : content.description;
  const highlights = isVerified ? groupNames : undefined;
  const disclaimer = isVerified ? undefined : content.disclaimer;
  const learnMoreLabel = learnMoreUrl ? content.learnMoreLabel : undefined;
  const learnMorePalette = isVerified ? 'black' : 'outline';
  const switchAccountLabel = isSwitchAccountVisible ? content.switchAccountLabel : undefined;

  const lockupPartnerLogo = isVerified && isCompactLayout ? undefined : partnerLogo;
  const inlineDisclaimer = isCompactLayout ? undefined : disclaimer;
  const footerDisclaimer = isCompactLayout ? disclaimer : undefined;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const openLearnMore = useCallback(() => {
    if (!learnMoreUrl) {
      return;
    }

    track('idme.verification_page.learnmore_tapped', { entry_source: entrySource });
    openURL(learnMoreUrl);
  }, [entrySource, learnMoreUrl, openURL, track]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const contentStyles = [styles.content, match([styles.contentXS, styles.contentSM])];
  const descriptionStyles = [styles.description, match([styles.centered, undefined])];
  const actionsStyles = match([styles.actionsXS, styles.actionsSM]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <View style={contentStyles}>
        <IdMeLogoLockup withVerifiedBadge={isVerified} partnerLogo={lockupPartnerLogo} />

        <BodyText sizeMatch={['18']} style={descriptionStyles}>
          <HighlightWords
            sizeMatch={['18']}
            text={description}
            highlights={highlights}
            style={descriptionStyles}
          />

          {inlineDisclaimer ? ` ${inlineDisclaimer}` : null}
        </BodyText>
      </View>

      <View style={actionsStyles}>
        {footerDisclaimer ? (
          <BodyText sizeMatch={['12']} style={styles.footerDisclaimer}>
            {footerDisclaimer}
          </BodyText>
        ) : null}

        {isVerified ? null : (
          <IdMeVerifyButton
            isLoading={isVerifyLoading}
            isDisabled={isAuthorizing}
            onPress={verify}
          />
        )}

        {learnMoreLabel ? (
          <Button
            size="large-wide"
            palette={learnMorePalette}
            style={styles.cta}
            onPress={openLearnMore}
          >
            {learnMoreLabel}
          </Button>
        ) : null}

        {switchAccountLabel ? (
          <Button
            size="large-wide"
            palette="outline"
            isLoading={isSigningOut}
            style={styles.cta}
            onPress={signOut}
          >
            {switchAccountLabel}
          </Button>
        ) : null}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flexGrow: 1,
    gap: theme.spacing['10'],
    paddingTop: theme.spacing['4'],
  },
  containerSM: {
    gap: theme.spacing['8'],
  },
  content: {
    gap: theme.spacing['4'],
  },
  contentXS: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['8'],
  },
  contentSM: {
    paddingHorizontal: theme.spacing['4'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  centered: {
    textAlign: 'center',
  },
  actionsXS: {
    gap: theme.spacing['2'],
    marginBottom: theme.spacing['2'],
    paddingHorizontal: theme.spacing['6'],
  },
  actionsSM: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['4'],
  },
  footerDisclaimer: {
    color: theme.colors.CHARCOAL,
    textAlign: 'center',
    marginBottom: theme.spacing['2'],
  },
  cta: {
    paddingHorizontal: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  verification: ReturnType<typeof useIdMeVerification>;
  content: ReturnType<typeof useIdMeVerificationContent>;
};

type RouteParams = {
  entry_source?: string;
};
